import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { NgTemplateOutlet } from '@angular/common';
import { Ag1ListSearchComponent } from '../ag1-list-search/ag1-list-search.component';
import { Ag1LoaderComponent } from '../ag1-loader/ag1-loader.component';
import { HumanizePipe } from '@app/core/pipes/humanize.pipe';
import { Ag1MillerColumnMixpanelEventTrackingService } from './ag1-miller-column-mixpanel-event-tracking.service';

export interface IMillerItem {
  itemTitle: string; // e.g. the name of the location
  itemIcon?: string; // e.g. the icon representation the document status
  value: any; // e.g. id of the location selected
  field?: string; //The field associated with the item (useful for subitems)
  itemSubTitle?: string; //e.g. Sub text of item eg. '500 PALLETS'
}

export interface ISelectedMillerItem {
  item: any; // value of the selected parent
  subItem: any; // value of the selected child
}
@Component({
  selector: 'ag1-miller-column',
  templateUrl: './ag1-miller-column.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Bind,
    Button,
    Tooltip,
    AngularSvgIconModule,
    NgTemplateOutlet,
    Ag1ListSearchComponent,
    Ag1LoaderComponent,
    HumanizePipe,
  ],
})
export class Ag1MillerColumnComponent implements OnChanges, OnInit {
  private cdr = inject(ChangeDetectorRef);
  private _millerColumnMixpanelTracking = inject(
    Ag1MillerColumnMixpanelEventTrackingService,
  );

  /** (Required) The name of the miller column (useful for mixpanel event tracking)*/
  @Input()
  feature: string;

  /** (Optional) Allow pre selection of item */
  @Input()
  selectedItem: IMillerItem = null;
  /** (Optional) Allow pre selection of nestedItem */
  @Input()
  selectedNestedItem: IMillerItem = null;

  /** (Required) Heading of the component**/
  @Input()
  label: string;
  /** (Required) Heading of the component**/
  @Input()
  subLabel: string;
  /** (Required) SubHeading of the component**/
  @Input()
  nestedLabel: string;
  /** (Required) List of items that will populate the component**/
  @Input()
  items: any[];
  @Input()
  nestedItems: any[];

  /**(Optional) Flag to control whether or not the miller column will have nested items or not*/
  @Input()
  hasNestedItems: boolean = true;

  /** (Required) Flag to control the loading spinner for items**/
  @Input()
  isItemsLoading: boolean;
  /** (Required) Flag to control the loading spinner for sub-items**/
  @Input()
  isSubItemsLoading: boolean;

  /** (Optional) Flag to allow for searching on items**/
  @Input()
  hasItemsSearch: boolean = true;
  /** (Optional) Flag to allow for searching on sub-items**/
  @Input()
  hasNestedItemsSearch: boolean = false;

  /** (Required) Tells the parent which items and/or sub-items were selected */
  @Output() itemSelectedEvent = new EventEmitter<ISelectedMillerItem>();

  @Input()
  resetItemLabel: string;

  // side panel
  millerColumnWidth: number = 220; // default in px
  showMillerColumn: boolean = true;
  cacheNameMillerColumnWidth: string = 'miller-column-width';

  // filtered list
  filteredItems: IMillerItem[] = [];
  filteredNestedItems: IMillerItem[] = [];

  constructor() {}

  ngOnInit(): void {
    this.loadCacheConfigs();
  }

  trackByItem(index: number, item: IMillerItem): string {
    // build compound key to use as a unique identifier
    if (item?.itemSubTitle && item?.itemTitle) {
      return `${item.itemTitle}-${item.itemSubTitle}-${index}`;
    }

    if (item?.itemTitle) {
      return `${item.itemTitle}-${index}`;
    }

    return index.toString();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.items) {
      this.filteredItems = this.items;
    }

    if (changes.nestedItems) {
      this.filteredNestedItems = this.nestedItems;
    }
  }

  isSelectedItem(item: IMillerItem): boolean {
    return JSON.stringify(this.selectedItem) === JSON.stringify(item.value);
  }

  loadCacheConfigs(): void {
    const millerColumnWidth: string = localStorage.getItem(
      this.cacheNameMillerColumnWidth,
    );

    if (millerColumnWidth) {
      this.millerColumnWidth = Number(JSON.parse(millerColumnWidth));
    } else {
      this.millerColumnWidth = 220;
    }
  }

  isSelectedNestedItem(nestedItem: IMillerItem): boolean {
    return (
      JSON.stringify(this.selectedNestedItem) ===
      JSON.stringify(nestedItem.value)
    );
  }

  refreshInterface(): void {
    this.cdr.detectChanges();
  }

  onMillerColumnResize(event: MouseEvent): void {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = this.millerColumnWidth;

    const onMouseMove = (moveEvent: MouseEvent): void => {
      const widthChange = moveEvent.clientX - startX;
      const newWidth = Math.max(startWidth + widthChange, 100); // Set a minimum width
      this.millerColumnWidth = newWidth;
      //Store the new width in cache
      localStorage.setItem(
        this.cacheNameMillerColumnWidth,
        JSON.stringify(newWidth),
      );
      this.refreshInterface();
    };

    const onMouseUp = (): void => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }

  onItemResetClick(): void {
    this.selectedItem = null;
    this.selectedNestedItem = null;
    this.itemSelectedEvent.emit({ item: null, subItem: null });
  }

  onItemClick(item: IMillerItem): void {
    // reset selections
    this.selectedItem = item.value;
    this.selectedNestedItem = null;

    this.itemSelectedEvent.emit({ item: item, subItem: null });

    //Mixpanel Event tracking
    this._millerColumnMixpanelTracking.trackTopLevelSelected(this);
  }

  onNestedItemClick(parentItem: IMillerItem, nestedItem: IMillerItem): void {
    // reset selections
    this.selectedNestedItem = nestedItem.value;

    this.itemSelectedEvent.emit({ item: parentItem, subItem: nestedItem });

    //Mixpanel Event tracking
    this._millerColumnMixpanelTracking.trackSubLevelSelected(this);
  }

  toggleMillerColumn(): void {
    this.showMillerColumn = !this.showMillerColumn;

    //Mixpanel Event tracking
    if (!this.showMillerColumn) {
      this._millerColumnMixpanelTracking.trackCollapseColumn(this);
    }
  }

  selectItemById(id: string | number, options?: { emitEvent?: boolean }): void {
    const match = this.items?.find((item) => String(item.value) === String(id));
    if (!match) {
      return;
    }

    if (options?.emitEvent === false) {
      this.selectedItem = match.value;
      this.selectedNestedItem = null;
      this.refreshInterface();
      return;
    }

    this.onItemClick(match);
    this.refreshInterface();
  }

  get resetLabel(): string {
    return this.resetItemLabel || `All ${this.label}`;
  }

  get resetSubLabel(): string {
    return this.subLabel;
  }
}
